"use client"

import { useCallback, useState, type FormEvent } from "react"
import { useTranslations } from "next-intl"
import { Search, Info, Sparkles } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Link } from "@/i18n/navigation"
import { getRedirectClick } from "@/lib/click"
import { useAppContext } from "@/contexts/app-context"
import { cleanAmazonUrl, normalizeUrl } from "@/app/amazon/_utils/amazon-url-utils"
import { AnimatedSection } from "../animated-section"

/** Search bar styled to match the hero's: input with an embedded icon-only submit button. */
function AmazonSearch() {
  const t = useTranslations("start.amazon")
  const { user, clientId, clientVersion } = useAppContext()
  const [term, setTerm] = useState("")

  const performSearch = useCallback(
    (e: FormEvent) => {
      e.preventDefault()
      const searchTerm = term.trim()
      if (!searchTerm) return
      const normalized = normalizeUrl(searchTerm)
      const destination = normalized ? cleanAmazonUrl(normalized) : `https://www.amazon.de/s?k=${encodeURIComponent(searchTerm)}`
      window.open(getRedirectClick(destination, user?.id, clientId, clientVersion), "_blank")
    },
    [term, user?.id, clientId, clientVersion],
  )

  return (
    <form onSubmit={performSearch}>
      <div className="relative">
        <Input
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          placeholder={t("searchPlaceholder")}
          className="h-12 rounded-xl bg-white/65 pr-12"
        />
        <Button
          type="submit"
          size="icon"
          aria-label={t("searchButton")}
          className="absolute right-1.5 top-1/2 h-9 w-9 -translate-y-1/2 rounded-lg"
        >
          <Search className="h-4 w-4" />
        </Button>
      </div>
    </form>
  )
}

function Notice() {
  const t = useTranslations("start.amazon")
  return (
    <div className="mt-4 flex items-start gap-2 text-sm text-muted-foreground">
      <Info className="mt-0.5 h-4 w-4 shrink-0" />
      <p>
        {t("noticeText")}{" "}
        <Link href="/shops/amazon" className="font-medium text-primary hover:underline">
          {t("noticeButton")}
        </Link>
      </p>
    </div>
  )
}

/** spotlight2 — reversed gradient, 2-column layout with copy left and search right. */
export function AmazonSpotlight2() {
  const t = useTranslations("start.amazon")
  return (
    <section className="py-16">
      <div className="container mx-auto px-4">
        <AnimatedSection className="mx-auto max-w-4xl">
          <div className="relative overflow-hidden rounded-3xl border border-white/15 p-8 md:p-12">
            <div
              aria-hidden
              className="absolute inset-0 -z-10"
              style={{ background: "linear-gradient(225deg, oklch(0.52 0.17 143 / 0.22), oklch(0.84 0.16 87 / 0.20) 50%, transparent)" }}
            />
            <div className="grid items-center gap-8 md:grid-cols-2">
              <div>
                <div className="mb-4 flex items-center gap-2 text-sm font-medium text-amber-500">
                  <Sparkles className="h-4 w-4" />
                  <span>Amazon</span>
                </div>
                <h2 className="text-3xl font-bold md:text-4xl">{t("headerTitle")}</h2>
                <p className="mt-2 text-lg text-muted-foreground">{t("headerSub")}</p>
              </div>
              <div>
                <AmazonSearch />
                <Notice />
              </div>
            </div>
          </div>
        </AnimatedSection>
      </div>
    </section>
  )
}
